import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { User } from '../../model/user';
import { Version } from '../../model/version';
import { Roulage } from '../../plugin-roulage/model/roulage';
import { RoulageService } from '../../plugin-roulage/roulage.service';
import { BookingService } from '../../services/booking.service';

const EXPANDED_KEY = 'mir.todayEventsBannerExpanded';

/**
 * Une commande de matériel du jour, réduite à ce que la bannière affiche.
 * Les réservations complètes portent une vingtaine de champs, dont les fils
 * de communication : rien de tout ça n'est utile ici.
 */
interface TodayBooking {
  id: number;
  eventName: string;
  start: string;
  end: string;
  // Les vehicules sont sortis du lot : c'est ce qu'on cherche d'abord quand
  // on se demande ce qui est engage aujourd'hui. Le champ `type` de la
  // ressource (VEHICLE / ROOM / TRAILER) donne le critere, plutot qu'une
  // lecture du nom qui varierait d'un SDIS a l'autre.
  vehicles: string[];
  others: string[];
  apr: number;
  pending: boolean;
  // Une commande qui ne se rattache a rien : ni numero Ecawin, ni roulage.
  // Elles sont nombreuses et parfaitement legitimes -- locations externes,
  // sorties, seances -- mais rien ne les relie a un evenement, donc rien ne
  // dit qui les a demandees ni pourquoi le vehicule sort. D'ou l'avertissement.
  unlinked: boolean;
}

@Component({
  selector: 'mir-today-events-banner',
  templateUrl: './today-events-banner.component.html',
  styleUrls: ['./today-events-banner.component.css'],
  standalone: false,
})
export class TodayEventsBannerComponent implements OnInit, OnDestroy {
  @Input()
  user: User;

  @Input()
  version: Version;

  // Événements du jour dont l'heure de fin n'est pas encore passée, triés
  // par heure de début -- events[0] est donc "le prochain" (ou celui en
  // cours), même logique que la bannière d'annonces actives.
  events: any[] = [];

  // Matériel commandé pour les événements du jour, encore d'actualité.
  bookings: TodayBooking[] = [];

  // Le résumé porte sur la journée entière, pas sur la première commande.
  // Compter la première seule était juste tant qu'il n'y en avait qu'une :
  // avec un roulage à 18:00 devant un cours à 18:30, ça annonçait
  // « 1 véhicule jusqu'à 21:00 » là où trois véhicules roulent jusqu'à 22:00.
  // Les véhicules sont comptés par nom distinct : le même engin commandé deux
  // fois dans la journée reste un véhicule engagé.
  vehiclesCount = 0;
  resourcesCount = 0;
  lastEnd: any = null;

  // Déplié ou replié, l'état est retenu d'une visite à l'autre : qui garde
  // l'oeil sur la journée -- les permanences -- n'a pas à rouvrir la bannière
  // à chaque retour sur le dashboard.
  expanded = localStorage.getItem(EXPANDED_KEY) === 'true';

  // Instant courant, rafraichi toutes les 30 secondes : c'est lui qui decide
  // de la pastille « En cours ». Sans horloge, un exercice commence a 17h00
  // resterait marque a venir toute la soiree pour qui laisse son dashboard
  // ouvert -- ce que font les permanences.
  now = Date.now();
  private clock: any;

  // Le matériel demande le module MATERIEL et le droit de lecture des
  // commandes ; les roulages, eux, ne demandent que le module ROULAGE : savoir
  // ce qui roule ce soir ne dépend d'aucun rôle. La bannière peut donc n'avoir
  // que sa moitié gauche, et il faut alors ne rien lier vers le module
  // commandes -- l'utilisateur n'y entrerait pas.
  showBookings = false;

  // Commandes dont la liste complete de ressources est depliee. Une commande
  // peut en porter une dizaine : seuls les vehicules sont montres d'emblee.
  private expandedBookings = new Set<number>();

  // Commandes retenues, indexees par identifiant d'evenement (code Ecawin, ou
  // identifiant de roulage prefixe). Un evenement peut en porter plusieurs :
  // sur l'annee du 610, 35 evenements en ont une, 8 en ont deux et un en a
  // quatre.
  private bookingsByEvent = new Map<string, TodayBooking[]>();

  // La modale n'existe dans le DOM que lorsqu'un evenement est choisi : la
  // liste des convoques peut compter plusieurs dizaines de lignes et il y a
  // autant d'evenements que la journee en contient.
  @ViewChild('participantsModal', { static: true }) participantsModal: any;
  selectedEvent: any;

  constructor(
    private bookingService: BookingService,
    private roulageService: RoulageService
  ) {}

  ngOnInit(): void {
    this.showBookings =
      !!this.version?.isAuthorized('MATERIEL') &&
      this.user.isAuthorized('OP_BOOKING_READ');
    const withRoulages = !!this.version?.isAuthorized('ROULAGE');
    if (!this.showBookings && !withRoulages) {
      return;
    }

    // La pastille « En cours » ne vaut que si l'heure avance : le dashboard
    // reste ouvert des heures sur les postes de permanence.
    this.clock = setInterval(() => (this.now = Date.now()), 30000);

    // Les trois sources sont isolées les unes des autres : elles viennent de
    // deux plugins, activés séparément d'un SDIS à l'autre, et
    // l'indisponibilité de l'une ne doit pas emporter les autres avec elle.
    const ecawin$ = this.showBookings
      ? this.bookingService.getTodayEcawin().pipe(catchError(() => of([])))
      : of([]);
    const roulages$ = withRoulages
      ? this.roulageService
          .getTodayRoulages()
          .pipe(catchError(() => of([] as Roulage[])))
      : of([] as Roulage[]);
    const reservations$ = this.showBookings
      ? this.bookingService.getTodayBookings().pipe(catchError(() => of([])))
      : of([] as any[]);

    forkJoin({
      ecawin: ecawin$,
      roulages: roulages$,
      reservations: reservations$,
    }).subscribe(
      (data: { ecawin: any; roulages: Roulage[]; reservations: any[] }) => {
        const now = Date.now();
        this.events = [
          ...((data.ecawin as any[]) || []),
          ...data.roulages.map((roulage) => this.asEvent(roulage)),
        ]
          .filter((e) => new Date(e.endDateDate).getTime() >= now)
          .sort(
            (a, b) =>
              new Date(a.startDateDate).getTime() -
              new Date(b.startDateDate).getTime()
          );
        this.bookings = this.collectBookings(
          data.reservations,
          this.roulagesByReservation(data.roulages),
          now
        );
        this.bookingsByEvent = this.indexByEvent(this.events, this.bookings);
        this.summarize(this.bookings);
      }
    );
  }

  ngOnDestroy(): void {
    clearInterval(this.clock);
  }

  /**
   * Vrai entre le début et la fin, bornes basses incluse et haute exclue.
   * `now` étant un champ rafraîchi par l'horloge, la vue suit sans que la
   * liste ait à être reconstruite.
   */
  isOngoing(start: any, end: any): boolean {
    const from = new Date(start).getTime();
    const to = new Date(end).getTime();
    return from <= this.now && this.now < to;
  }

  /**
   * Un roulage rendu à la forme d'un événement Ecawin, pour que la bannière
   * n'ait rien à savoir de plus : même champs, même commande de matériel
   * rattachée. Seul l'identifiant de cette commande est repris ici -- son
   * contenu vient désormais de `/today` comme celui de toutes les autres --
   * et elle est facultative : près de la moitié des roulages enregistrés n'en
   * portent aucune, et la ligne s'affiche alors sans lien.
   */
  private asEvent(roulage: Roulage): any {
    return {
      id: 'roulage-' + roulage.id,
      text: `${roulage.type} — ${roulage.vehicleName}`,
      startDateDate: roulage.startDate,
      endDateDate: roulage.endDate,
      // Ce qui vaut au roulage son picto de véhicule dans la liste.
      roulage: true,
      // Le `nip` sert de clé de suivi dans la liste des convoqués, et deux
      // utilisateurs peuvent ne pas en avoir : on retombe sur l'identifiant.
      participants: (roulage.participants || []).map((p) => ({
        ...p,
        nip: p.nip != null ? p.nip : 'u' + p.id,
      })),
      reservations:
        this.showBookings && roulage.reservationId
          ? [{ id: roulage.reservationId }]
          : [],
    };
  }

  /** Commandes de materiel rattachees a un evenement, dans l'ordre du jour. */
  bookingsOf(event: any): TodayBooking[] {
    return this.bookingsByEvent.get(event.id) || [];
  }

  isResourcesExpanded(bookingId: number): boolean {
    return this.expandedBookings.has(bookingId);
  }

  toggleResources(bookingId: number): void {
    if (!this.expandedBookings.delete(bookingId)) {
      this.expandedBookings.add(bookingId);
    }
  }

  /** Les roulages du jour, indexés par la commande qu'ils portent. */
  private roulagesByReservation(roulages: Roulage[]): Map<number, Roulage> {
    const byReservation = new Map<number, Roulage>();
    for (const roulage of roulages || []) {
      if (roulage.reservationId) {
        byReservation.set(roulage.reservationId, roulage);
      }
    }
    return byReservation;
  }

  /**
   * La colonne matériel se lit sur `/today`, pas sur les événements.
   *
   * Elle était auparavant reconstruite depuis les événements Ecawin et les
   * roulages, ce qui la rendait aveugle à tout ce qui ne s'y rattache pas :
   * sur l'instance CO, 3925 des 7276 commandes n'ont aucun numéro Ecawin --
   * locations externes, sorties, séances -- et n'apparaissaient nulle part.
   * Un numéro Ecawin sans événement du jour en face (cours sur plusieurs
   * jours, valeurs saisies à la main comme « Privé ») produisait le même trou.
   *
   * Deux précautions restent sur la donnée réelle : une commande dont l'heure
   * de fin est passée n'engage plus rien, et un roulage prête son libellé à sa
   * commande -- le `eventName` enregistré y est la liste des participants, sur
   * plusieurs lignes, illisible dans une bannière.
   */
  private collectBookings(
    reservations: any[],
    roulages: Map<number, Roulage>,
    now: number
  ): TodayBooking[] {
    const byId = new Map<number, TodayBooking>();
    for (const res of reservations || []) {
      if (res.state === 'REFUSED') {
        continue;
      }
      if (new Date(res.end).getTime() < now) {
        continue;
      }
      const roulage = roulages.get(res.id);
      byId.set(res.id, {
        id: res.id,
        eventName: roulage ? roulage.type : res.eventName,
        start: res.start,
        end: res.end,
        vehicles: this.namesOfType(res.resources, true),
        others: this.namesOfType(res.resources, false),
        apr: res.apr || 0,
        pending: res.state === 'SUBMITTED',
        unlinked: this.isUnlinked(res, roulage),
      });
    }
    return [...byId.values()].sort(
      (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()
    );
  }

  /**
   * Une commande n'est rattachée à rien quand elle n'a pas de numéro Ecawin et
   * qu'aucun roulage ne la porte.
   *
   * Le type système `ROULAGE` double le test sur les roulages du jour : le
   * plugin roulage peut être éteint sur un SDIS où d'anciennes commandes de
   * roulage subsistent, et il ne faut pas les signaler comme orphelines.
   */
  private isUnlinked(res: any, roulage: Roulage | undefined): boolean {
    return (
      !roulage &&
      !(res.ecawin || '').trim() &&
      res.reservationType?.key !== 'ROULAGE'
    );
  }

  /** Ce que la ligne repliée annonce : tout le matériel du jour, d'un coup. */
  private summarize(bookings: TodayBooking[]): void {
    const vehicles = new Set<string>();
    const resources = new Set<string>();
    let last: any = null;
    for (const booking of bookings) {
      booking.vehicles.forEach((name) => vehicles.add(name));
      booking.vehicles.forEach((name) => resources.add(name));
      booking.others.forEach((name) => resources.add(name));
      if (!last || new Date(booking.end).getTime() > new Date(last).getTime()) {
        last = booking.end;
      }
    }
    this.vehiclesCount = vehicles.size;
    this.resourcesCount = resources.size;
    this.lastEnd = last;
  }

  private indexByEvent(
    events: any[],
    bookings: TodayBooking[]
  ): Map<string, TodayBooking[]> {
    const kept = new Set(bookings.map((b) => b.id));
    const index = new Map<string, TodayBooking[]>();
    for (const event of events) {
      const forEvent = (event.reservations || [])
        .filter((res: any) => kept.has(res.id))
        .map((res: any) => bookings.find((b) => b.id === res.id));
      if (forEvent.length) {
        index.set(event.id, forEvent);
      }
    }
    return index;
  }

  private namesOfType(resources: any[], vehicles: boolean): string[] {
    return (resources || [])
      .filter((r: any) => (r.type === 'VEHICLE') === vehicles)
      .map((r: any) => r.name);
  }

  toggle(): void {
    this.expanded = !this.expanded;
    localStorage.setItem(EXPANDED_KEY, String(this.expanded));
  }

  showParticipants(event: any): void {
    this.selectedEvent = event;
    this.participantsModal.show();
  }
}
